/**
 * Which browser this is, and therefore how Larder gets installed on it.
 *
 * Browser sniffing is normally a smell. Here it is unavoidable and it is the
 * whole feature: there is no single way to install a PWA. Chromium fires
 * `beforeinstallprompt` and one button does it. Safari on iOS has no such event
 * and never will — every install there is a human following a Share-sheet
 * sequence, so the app has to *describe* it, and describing the wrong sequence
 * is worse than describing none.
 *
 * Kept as a pure function of the UA string so the branches are testable. The
 * alternative — reading `navigator` inline at the call site — is how the iPad
 * case quietly stops working and nobody notices for a year.
 */

export type Os = "ios" | "android" | "macos" | "windows" | "other";
export type Browser =
  | "safari"
  | "chrome"
  | "edge"
  | "firefox"
  | "samsung"
  | "opera"
  | "other";

export interface Platform {
  os: Os;
  browser: Browser;
  /** A phone or tablet — the device this app is actually for. */
  handheld: boolean;
  /**
   * Could `beforeinstallprompt` fire here? Chromium-family, and NOT on iOS:
   * every iOS browser is WebKit underneath by Apple's rule, so Chrome there is
   * Safari wearing a hat and has no install event either.
   */
  supportsPrompt: boolean;
  /** Can this browser install a PWA at all, by any route including manual? */
  installable: boolean;
}

/**
 * @param ua       `navigator.userAgent`
 * @param touchPoints `navigator.maxTouchPoints` — the ONLY way to tell an iPad
 *   from a Mac since iPadOS 13, which reports itself as "Macintosh" in the UA.
 *   Without this an iPad is told to use the File menu, which it does not have.
 */
export function detectPlatform(ua: string, touchPoints = 0): Platform {
  const iPadAsMac = /Macintosh/.test(ua) && touchPoints > 1;
  const os: Os =
    /iPhone|iPad|iPod/.test(ua) || iPadAsMac
      ? "ios"
      : /Android/.test(ua)
        ? "android"
        : /Macintosh|Mac OS X/.test(ua)
          ? "macos"
          : /Windows/.test(ua)
            ? "windows"
            : "other";

  const browser = readBrowser(ua, os);
  const handheld = os === "ios" || os === "android";
  // Samsung Internet and Opera are Chromium and do fire the event; Firefox
  // never has on any platform.
  const supportsPrompt =
    os !== "ios" &&
    (browser === "chrome" ||
      browser === "edge" ||
      browser === "samsung" ||
      browser === "opera");

  return {
    os,
    browser,
    handheld,
    supportsPrompt,
    installable:
      supportsPrompt ||
      // Safari installs by hand: Share sheet on iOS, Add to Dock on macOS 14+.
      (browser === "safari" && (os === "ios" || os === "macos")) ||
      // Firefox for Android has "Install" in its menu. Desktop Firefox has
      // nothing at all, which is why this is not simply `browser !== "other"`.
      (browser === "firefox" && os === "android"),
  };
}

function readBrowser(ua: string, os: Os): Browser {
  if (os === "ios") {
    // On iOS the engine is always WebKit, so these tokens describe the shell —
    // which still matters, because only Safari's share sheet has the item.
    if (/CriOS/.test(ua)) return "chrome";
    if (/FxiOS/.test(ua)) return "firefox";
    if (/EdgiOS/.test(ua)) return "edge";
    if (/OPT\//.test(ua)) return "opera";
    return "safari";
  }
  // Order matters: every one of these also says "Chrome" and most also say
  // "Safari". Most specific first, or everything is Chrome.
  if (/SamsungBrowser/.test(ua)) return "samsung";
  if (/Edg\//.test(ua)) return "edge";
  if (/OPR\/|Opera/.test(ua)) return "opera";
  if (/Firefox\//.test(ua)) return "firefox";
  if (/Chrome\/|Chromium\//.test(ua)) return "chrome";
  if (/Safari\//.test(ua)) return "safari";
  return "other";
}

/** A picture worth drawing next to a step. Rendered by the install screen. */
export type StepIcon = "share" | "menu" | "plus" | "install" | "phone";

export interface Step {
  icon: StepIcon;
  /** The instruction. The `strong` part is the thing to tap. */
  text: string;
  strong: string;
}

export interface Guide {
  /** What this browser can actually do, said in one line. */
  title: string;
  steps: Step[];
  /** The label on the button that finishes the step. */
  confirm: string;
  /**
   * True when the confirm button really installs the app (Chromium), false
   * when the user has to do it themselves and we can only take their word.
   */
  automatic: boolean;
}

/**
 * The instructions for a platform, assuming no native prompt is available.
 *
 * `canPrompt` is passed separately rather than read from the platform, because
 * Chromium withholds `beforeinstallprompt` in cases the UA cannot predict —
 * already installed, criteria not yet met, an unsupported context. When the
 * event is genuinely in hand the button IS the guide; when it never arrives on
 * a Chromium browser, the menu route still works and is what gets described.
 */
export function installGuide(p: Platform, canPrompt: boolean): Guide {
  if (canPrompt) {
    return {
      title: "One tap and it's on your home screen.",
      steps: [],
      confirm: "Install Larder",
      automatic: true,
    };
  }

  if (p.os === "ios" && p.browser === "safari") {
    return {
      title: "Two taps in Safari's Share menu.",
      steps: [
        { icon: "share", text: "Tap", strong: "Share" },
        { icon: "plus", text: "Scroll down and tap", strong: "Add to Home Screen" },
        { icon: "install", text: "Tap", strong: "Add" },
      ],
      confirm: "Done — it's on my home screen",
      automatic: false,
    };
  }

  if (p.os === "ios") {
    // Chrome and Firefox on iOS cannot do this, and pretending otherwise sends
    // someone hunting a Share menu for an item that is not in it.
    return {
      title: "This one needs Safari.",
      steps: [
        { icon: "share", text: "Open this page in", strong: "Safari" },
        { icon: "plus", text: "Tap Share, then", strong: "Add to Home Screen" },
      ],
      confirm: "Done — it's on my home screen",
      automatic: false,
    };
  }

  if (p.os === "android") {
    return {
      title: "It's two taps in the browser menu.",
      steps: [
        { icon: "menu", text: "Open the browser", strong: "menu" },
        {
          icon: "plus",
          text: "Tap",
          strong: p.browser === "firefox" ? "Install" : "Add to Home screen",
        },
      ],
      confirm: "Done — it's on my home screen",
      automatic: false,
    };
  }

  if (p.browser === "safari") {
    return {
      title: "Safari keeps it in your Dock.",
      steps: [
        { icon: "share", text: "Open the", strong: "Share menu" },
        { icon: "plus", text: "Choose", strong: "Add to Dock" },
      ],
      confirm: "Done — it's in my Dock",
      automatic: false,
    };
  }

  if (p.browser === "firefox") {
    // Desktop Firefox cannot install a PWA by any route. The honest move is to
    // stop selling the install and sell the phone, which is the better device
    // for this app anyway — the fridge is not next to the desk.
    return {
      title: "Larder belongs on your phone.",
      steps: [
        { icon: "phone", text: "Open", strong: "larder.web.app" },
        { icon: "plus", text: "on your phone and", strong: "add it to your home screen" },
      ],
      confirm: "Got it",
      automatic: false,
    };
  }

  return {
    title: "Add Larder to your desktop.",
    steps: [
      { icon: "install", text: "Look for the", strong: "install icon" },
      { icon: "plus", text: "in the address bar, and choose", strong: "Install" },
    ],
    confirm: "Done — it's installed",
    automatic: false,
  };
}

/**
 * Is the app running installed right now?
 *
 * Four checks because no single one covers the field: `display-mode` is the
 * standard and is what Android reports, `navigator.standalone` is the iOS
 * original and is still the only signal Safari gives, and an Android WebAPK
 * launch arrives with an `android-app://` referrer.
 *
 * `minimal-ui` and `fullscreen` count. The manifest asks for `standalone`, but
 * a browser is allowed to grant a neighbouring mode, and someone who installed
 * the app should never be asked to install it again.
 */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const ios = (window.navigator as { standalone?: boolean }).standalone === true;
  const modes = ["standalone", "fullscreen", "minimal-ui"];
  const display = modes.some((m) => window.matchMedia(`(display-mode: ${m})`).matches);
  return ios || display || document.referrer.startsWith("android-app://");
}
